import * as moment from "moment";
import { convertTimeUnit } from "./time-converter.function";

/**
 * Formats time duration in a human-readable format
 * Automatically chooses the most appropriate unit
 */
export function formatDuration(totalSeconds: number): string {
  if (!totalSeconds || totalSeconds === 0) return '';

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    if (minutes === 0) return `${hours}h`;
    return `${hours}h ${minutes}m`;
  } else if (minutes > 0) {
    if (seconds === 0) return `${minutes}m`;
    return `${minutes}m ${seconds}s`;
  } else {
    return `${seconds}s`;
  }
}

/**
 * Gets the best unit for displaying a time value
 * Returns the unit that results in the most readable number
 */
export function getBestTimeUnit(seconds: number): string {
  if (seconds >= 3600 && seconds % 3600 === 0) return 'hours';
  if (seconds >= 60 && seconds % 60 === 0) return 'minutes';
  return 'seconds';
}

/**
 * Converts seconds to the best display unit and returns both value and unit
 */
export function getOptimalTimeDisplay(seconds: number): { value: number; unit: string } {
  const bestUnit = getBestTimeUnit(seconds);
  const value = convertTimeUnit(seconds, 'seconds', bestUnit);
  return { value, unit: bestUnit };
}

/**
 * Combine a Date object and a "HH:mm:ss" (or "HH:mm") time string,
 * and return the epoch time (milliseconds since 1970-01-01 UTC).
 *
 * @param {Date} dateObj - The date part (any valid JS Date)
 * @param {string} timeStr - Time string like "14:30:05" or "14:30"
 * @returns {number} epochMillis
 */
export function combineDateStringAndTimeToEpoch(dateObj, timeStr) {
  // Parse time parts
  const parts = timeStr.split(":");
  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);
  const seconds = parts[2] !== undefined ? parseInt(parts[2], 10) : 0;
  const m = moment(dateObj)
    .hours(hours)
    .minutes(minutes)
    .seconds(seconds)
    .milliseconds(0);

  // Milliseconds since epoch
  return m.valueOf();

}

export function formatTime(raceTime: string) {
  let today = moment();
  return today.diff(moment(raceTime), 'minutes');
}

export function hasDatePassed(dateTime: string): boolean {
  return moment(dateTime).isBefore(moment());
}

/**
 * Format YYYY-MM-DD date string to human-readable format based on language
 * @param dateString - Date in YYYY-MM-DD format
 * @param language - Language code (e.g., 'en', 'fr', 'es')
 * @returns Formatted date string
 */
export function formatDate(dateString: string, language: string = 'en'): string {
  if (!dateString) return '';
  
  // Set moment locale based on language
  const localeMap: { [key: string]: string } = {
    'en': 'en',
    'fr': 'fr',
    'es': 'es',
    'de': 'de',
    'pt': 'pt',
    'ar': 'ar',
    'zh': 'zh-cn',
    'ja': 'ja',
    'it': 'it',
    'ru': 'ru',
    'nl': 'nl',
    'tr': 'tr',
    'ko': 'ko',
    'pl': 'pl'
  };
  
  const locale = localeMap[language] || 'en';
  
  try {
    // Set the locale for this operation
    moment.locale(locale);
    
    // Parse and format the date
    const formattedDate = moment(dateString).format('LL'); // Long date format (e.g., "January 30, 2026")
    
    // Reset to default locale if needed
    moment.locale('en');
    
    return formattedDate;
  } catch (error) {
    console.error('Error formatting date:', error);
    return dateString;
  }
}